export namespace CSVFactory {
    /**
     * Escapes and formats a field for CSV output
     * Handles special characters, line breaks, and ensures cross-platform compatibility
     *
     * @param field - The field content to escape
     * @returns The properly escaped and formatted field for CSV
     */
    export function escapeCsvField(field: string): string {
        if (!field) return "";

        // First normalize line breaks to a standard format (\n)
        // This handles cross-platform line break differences (\r\n from Windows, \r from old Mac)
        let escaped = field.replace(/\r\n|\r/g, "\n");

        // Replace double quotes with two double quotes (CSV standard)
        escaped = escaped.replace(/"/g, `""`);

        // Wrap in quotes to handle commas, line breaks, and special characters
        // The quotes ensure that the entire field is treated as a single value
        // even if it contains commas, line breaks, or special characters
        return `"${escaped}"`;
    }

    /**
     * Converts an array of objects to CSV format
     *
     * @param data - Array of objects to convert to CSV
     * @param headers - Optional custom headers (uses object keys if not provided)
     * @returns CSV formatted string
     */
    export function objectsToCSV<T extends Record<string, any>>(
        data: T[],
        headers?: { [K in keyof T]?: string }
    ): string {
        if (!data || data.length === 0) {
            return "";
        }

        // Get all unique keys from the objects
        const allKeys = Array.from(
            new Set(
                data.flatMap(obj => Object.keys(obj))
            )
        ) as (keyof T)[];

        // Create header row
        const headerRow = allKeys.map(key =>
            escapeCsvField(headers?.[key] as string || String(key))
        ).join(",");

        // Create data rows
        const dataRows = data.map(obj =>
            allKeys.map(key =>
                escapeCsvField(obj[key] !== undefined ? String(obj[key]) : "")
            ).join(",")
        );

        // Combine header and rows
        return [headerRow, ...dataRows].join("\n");
    }

    /**
     * Creates and downloads a CSV file
     *
     * @param content - CSV content as string
     * @param filename - Name for the downloaded file
     * @returns Promise that resolves when download is initiated or rejects with an error
     */
    export function downloadCSV(content: string, filename: string): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                if (!content) {
                    reject(new Error("No content provided for CSV export"));
                    return;
                }

                // Add UTF-8 BOM (Byte Order Mark) for better cross-platform compatibility
                // This helps Excel and other applications correctly identify the file as UTF-8
                const BOM = "\uFEFF";
                const csvWithBOM = BOM + content;

                try {
                    // Explicitly set charset to UTF-8 to ensure proper handling of special characters
                    const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8;" });
                    const url = URL.createObjectURL(blob);

                    // Create a temporary link element to trigger download
                    const link = document.createElement("a");
                    link.setAttribute("href", url);
                    link.setAttribute("download", filename);
                    link.style.visibility = "hidden";

                    document.body.appendChild(link);

                    // Use try-catch for the click operation as it might fail in some browsers
                    try {
                        link.click();
                        document.body.removeChild(link);

                        // Clean up by revoking the object URL
                        // Use try-catch in case URL.revokeObjectURL is not available (e.g., in tests)
                        setTimeout(() => {
                            try {
                                URL.revokeObjectURL(url);
                            } catch (error) {
                                console.warn("Could not revoke object URL:", error);
                            }
                        }, 100);

                        resolve();
                    } catch (clickError) {
                        document.body.removeChild(link);
                        try {
                            URL.revokeObjectURL(url);
                        } catch (error) {
                            console.warn("Could not revoke object URL:", error);
                        }
                        reject(new Error(`Failed to initiate download: ${clickError.message}`));
                    }
                } catch (blobError) {
                    reject(new Error(`Failed to create Blob for CSV: ${blobError.message}`));
                }
            } catch (error) {
                reject(new Error(`CSV download failed: ${error.message}`));
            }
        });
    }
}
